/**
 * An organization's own bill — the upcoming-invoice banner and putting it away.
 *
 * Everything else about billing is `admin-billing.ts` and is system admin only. This file talks to
 * `api/billing`, which takes no organization anywhere in its shape: the server reads the caller's
 * claim, so there is no id for this client to pass and none to get wrong.
 *
 * **Responses are normalised, not cast**, for the same reason as `admin-billing.ts`: web and the API
 * deploy on separate workflows off the same push, so a new build meets an older server regularly.
 * The worst a missing field can do here is leave the banner saying less, never break the page it
 * sits on.
 */

import { ApiError, get, post } from './api';
import {
  NO_BILLING_NOTICE,
  NO_INVOICES,
  type MyBillingNotice,
  type MyInvoice,
  type MyInvoices,
} from '../types/billing';

const BASE_URL = '/api/billing';

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const asInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;

const asBool = (value: unknown): boolean => value === true;

/**
 * A link the banner will put behind a Pay button, or null.
 *
 * Only `http:` and `https:` survive. The URL comes from Stripe by way of our own API and is not
 * expected to be anything else, but it ends up in an `href` a coach clicks, and `javascript:` in an
 * href is script execution. A boundary that hands a URL to the DOM checks the scheme; the cost is
 * three lines and the failure mode without it is not recoverable.
 */
const asHttpUrl = (value: unknown): string | null => {
  const raw = asString(value);
  if (!raw) return null;

  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? raw : null;
  } catch {
    return null;
  }
};

const normalizeNotice = (raw: unknown): MyBillingNotice => {
  if (!raw || typeof raw !== 'object') return NO_BILLING_NOTICE;
  const value = raw as Record<string, unknown>;

  const hasBill = asBool(value.hasBill);
  const invoiceId = asString(value.invoiceId);

  // A notice with nothing to dismiss is not a notice. The banner's only action is keyed by invoice
  // id, so `hasBill` without one would render a card whose button could not be pressed.
  if (!hasBill || !invoiceId) return NO_BILLING_NOTICE;

  const isOverdue = asBool(value.isOverdue);

  return {
    hasBill: true,
    invoiceId,
    number: asString(value.number),
    amountCents: asInt(value.amountCents),

    /*
      Falls back to the face value on a server too old to send the balance, which is what that
      server already meant by the bill. It can only ever overstate, and an overstated figure on a
      part-paid invoice is the behaviour this field replaced rather than a new failure.
    */
    amountDueCents: asInt(value.amountDueCents) ?? asInt(value.amountCents),
    currency: asString(value.currency),
    dueOn: asString(value.dueOn),
    isOverdue,
    daysOverdue: Math.max(0, asInt(value.daysOverdue) ?? 0),
    isDismissed: asBool(value.isDismissed),

    // Defaults to false, the safe direction: an older server that does not send the field leaves a
    // banner that cannot be put away, rather than offering a dismiss that silently does nothing.
    // Overdue is never dismissible whatever the server said, which is the same rule the server
    // applies — stated twice on purpose, because it is the one that stops a coach hiding a bill
    // that is already late.
    canDismiss: asBool(value.canDismiss) && !isOverdue,

    hostedInvoiceUrl: asHttpUrl(value.hostedInvoiceUrl),
  };
};

/**
 * The one bill worth telling this organization's admin about.
 *
 * **A 403 and a 404 both answer "nothing to show" rather than throwing.** A 403 is a coach whose
 * role cannot see billing, which is an ordinary fact about that person and not a failure; a 404 is
 * a web build that has reached an API deployed a few minutes behind it and has no such route yet.
 * Neither is worth an error, and a banner is the last thing that should interrupt a page. Anything
 * else throws and the caller decides.
 */
export const getMyBillingNotice = async (): Promise<MyBillingNotice> => {
  try {
    return normalizeNotice(await get<unknown>(`${BASE_URL}/me`));
  } catch (error) {
    if (error instanceof ApiError && (error.statusCode === 403 || error.statusCode === 404)) {
      return NO_BILLING_NOTICE;
    }
    throw error;
  }
};

/** Puts the notice for one invoice away, for this person alone. Answers with what stands after. */
export const dismissBillingNotice = async (invoiceId: string): Promise<MyBillingNotice> =>
  normalizeNotice(await post<unknown>(`${BASE_URL}/me/dismiss`, { invoiceId }));

const DISPLAY_STATUSES: readonly MyInvoice['displayStatus'][] = [
  'open',
  'draft',
  'overdue',
  'paid',
  'void',
  'uncollectible',
];

const normalizeInvoice = (raw: unknown): MyInvoice | null => {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;

  const id = asString(value.id);
  // No id, no row: every action on this screen is keyed by it, and a link to nowhere is worse
  // than one fewer line in a table.
  if (!id) return null;

  const status = value.displayStatus;

  return {
    id,
    number: asString(value.number),
    issuedOn: asString(value.issuedOn) ?? '',
    dueOn: asString(value.dueOn) ?? '',
    amountCents: asInt(value.amountCents) ?? 0,
    amountRemainingCents: asInt(value.amountRemainingCents) ?? 0,
    currency: asString(value.currency) ?? 'USD',
    /*
      Falls back to `open` rather than to a bare unknown string. An unrecognised status is one this
      build cannot render, and `open` is the reading that keeps an unpaid bill visible - the safe
      direction for a customer, who would rather be shown a bill they have paid than miss one they
      have not.
    */
    displayStatus: DISPLAY_STATUSES.includes(status as MyInvoice['displayStatus'])
      ? (status as MyInvoice['displayStatus'])
      : 'open',
    daysOverdue: asInt(value.daysOverdue) ?? 0,
    paidAt: asString(value.paidAt),
    payUrl: asHttpUrl(value.payUrl),
    pdfUrl: asHttpUrl(value.pdfUrl),
  };
};

/**
 * Every invoice this organization has, newest first.
 *
 * An organization with no billing set up gets an empty list rather than an error: a billing page
 * that breaks for a customer who has never been charged is worse than one that says so.
 */
export const getMyInvoices = async (signal?: AbortSignal): Promise<MyInvoices> => {
  const raw = await get<unknown>(`${BASE_URL}/me/invoices`, { signal });
  if (!raw || typeof raw !== 'object') return NO_INVOICES;

  const value = raw as Record<string, unknown>;
  const items = Array.isArray(value.items) ? value.items : [];

  return {
    items: items
      .map(normalizeInvoice)
      .filter((invoice): invoice is MyInvoice => invoice !== null),
    outstandingCents: asInt(value.outstandingCents) ?? 0,
    currency: asString(value.currency) ?? 'USD',
    isStripeManaged: asBool(value.isStripeManaged),
  };
};
